import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { siteImages, CONTACT_INFO } from '../data/mock';
import { Shield, Award, Truck, Heart, Zap, Users, CheckCircle, ArrowRight, Star, Target, Leaf, Clock } from 'lucide-react';

const WhyChooseUsPage = () => {
  useEffect(() => {
    document.title = 'Why Choose Us | Keto Vybe';
  }, []);

  const reasons = [
    {
      icon: Shield,
      title: 'Quality You Can Trust',
      description: 'Every product is third-party tested for purity, potency, and safety. We never compromise on quality.',
      color: 'vybe'
    },
    {
      icon: Leaf,
      title: 'Keto-Optimized Formulas',
      description: 'Our supplements are specifically designed for the ketogenic lifestyle. Zero or minimal carbs, maximum results.',
      color: 'cyan'
    },
    {
      icon: Award,
      title: 'Made in USA',
      description: 'All products are manufactured in GMP-certified facilities in the United States.',
      color: 'coral'
    },
    {
      icon: Zap,
      title: 'Science-Backed',
      description: 'Our formulas are based on the latest research and developed by nutrition experts.',
      color: 'vybe'
    },
    {
      icon: Truck,
      title: 'Fast, Free Shipping',
      description: 'Free shipping on orders over $99. Most orders ship within 1-2 business days.',
      color: 'cyan'
    },
    {
      icon: Heart,
      title: '30-Day Guarantee',
      description: 'Not satisfied? Return unopened products within 30 days for a full refund.',
      color: 'coral'
    }
  ];

  const values = [
    'Clean, transparent ingredients',
    'No artificial colors or flavors',
    'Non-GMO ingredients',
    'No harmful fillers',
    'Gluten-free formulas',
    'Rigorous quality testing'
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-20">
        {/* Banner */}
        <section className="relative h-72 lg:h-80 overflow-hidden">
          <img 
            src={siteImages.heroMain}
            alt="Why Choose Us"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-vybe-400 font-semibold text-sm tracking-wider uppercase mb-3 block">The Vybe Difference</span>
              <h1 className="font-display text-4xl lg:text-5xl text-white mb-3">
                Why Choose Keto Vybe
              </h1>
              <p className="text-slate-400 max-w-md mx-auto">
                Premium supplements for your keto journey
              </p>
            </div>
          </div>
        </section>

        {/* Main Reasons */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reasons.map((reason, i) => (
                <div key={i} className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 hover:border-vybe-500/30 transition-all">
                  <div className={`w-14 h-14 bg-${reason.color}-600/20 rounded-xl flex items-center justify-center mb-5`}>
                    <reason.icon className={`w-7 h-7 text-${reason.color}-400`} />
                  </div>
                  <h3 className="font-bold text-white text-lg mb-2">{reason.title}</h3>
                  <p className="text-slate-400 text-sm">{reason.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Promise */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-cyan-400 font-semibold text-sm tracking-wider uppercase mb-4 block">Our Promise</span>
                <h2 className="font-display text-3xl text-white mb-6">
                  Committed to Your Success
                </h2>
                <p className="text-slate-400 leading-relaxed mb-6">
                  At Keto Vybe, we're more than just a supplement company. We're your partners in health. 
                  Our mission is to provide the highest quality keto supplements that actually work.
                </p>
                <p className="text-slate-400 leading-relaxed mb-8">
                  We believe everyone deserves access to premium supplements without the premium price tag. 
                  That's why we focus on what matters: quality ingredients, effective formulas, and real results.
                </p>
                <ul className="grid grid-cols-2 gap-3">
                  {values.map((value, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-vybe-400 flex-shrink-0" />
                      {value}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <div className="rounded-3xl overflow-hidden">
                  <img 
                    src={siteImages.lifestyleSection}
                    alt="Our promise"
                    className="w-full h-80 object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-vybe-600 to-coral-500 rounded-2xl p-6">
                  <div className="text-center">
                    <div className="text-3xl font-black text-white">100%</div>
                    <div className="text-sm text-white/80">Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { value: '12+', label: 'Premium Products' },
                { value: '6', label: 'Categories' },
                { value: 'USA', label: 'Made In' },
                { value: '30', label: 'Day Guarantee' }
              ].map((stat, i) => (
                <div key={i}>
                  <div className="text-4xl font-black text-white mb-2">{stat.value}</div>
                  <div className="text-slate-400 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Company Info */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-br from-vybe-600/20 to-slate-800/50 rounded-2xl p-8 border border-vybe-500/30">
              <Target className="w-12 h-12 text-vybe-400 mx-auto mb-4" />
              <h3 className="font-display text-xl text-white mb-2">{CONTACT_INFO.corpName}</h3>
              <p className="text-slate-400 mb-6">
                A company dedicated to helping people thrive on their keto journey through premium supplementation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/shop"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-vybe-600 to-vybe-500 text-white font-bold rounded-full"
                >
                  Shop Now
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-700 text-white font-semibold rounded-full hover:bg-slate-600 transition-all"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default WhyChooseUsPage;
